import axios from "axios";
import { Search, SlidersHorizontal, Sparkles, Wind, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import leaf from "../../Assets/Images/leaf.png";
import PackageBookingModal from "../../components/UserSideComponents/packages/PackageBookingModal";
import PackageCard, { PackageCardSkeleton } from "../../components/UserSideComponents/packages/PackageCard";
import { usePackageBooking } from "../../context/PackageBookingContext";

function useDebounce(value, delay = 350) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return debounced;
}

const PETALS = [
    { top: "8%", left: "3%", size: 38, delay: 0, dur: 7 },
    { top: "15%", left: "92%", size: 44, delay: 1.5, dur: 9 },
    { top: "55%", left: "1%", size: 42, delay: 3, dur: 11 },
    { top: "70%", left: "95%", size: 36, delay: 2, dur: 8 },
    { top: "35%", left: "96%", size: 32, delay: 4.5, dur: 10 },
    { top: "88%", left: "4%", size: 40, delay: 1, dur: 6 },
];

const FloatingPetals = () => (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {PETALS.map((p, i) => (
            <div
                key={i}
                style={{
                    position: "absolute",
                    top: p.top,
                    left: p.left,
                    width: p.size,
                    height: p.size,
                    opacity: 0.2,
                    animation: `petalFloat ${p.dur}s ease-in-out ${p.delay}s infinite alternate`,
                }}
            >
                <img src={leaf} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
            </div>
        ))}
    </div>
);

const CategoryPill = ({ label, active, onClick }) => (
    <button
        onClick={onClick}
        className={`
      relative px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest
      border transition-all duration-200 whitespace-nowrap
      ${active
                ? "bg-[#2B1A14] border-[#2B1A14] text-[#f5e8e2] shadow-md"
                : "bg-white/70 border-[#9b3a1f]/20 text-[#6b4535] hover:border-[#9b3a1f]/50 hover:bg-white"
            }
    `}
    >
        {label}
    </button>
);

const EmptyState = ({ query }) => (
    <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-24 h-24 rounded-full bg-[#f0ddd5] flex items-center justify-center mb-6">
            <Wind className="w-10 h-10 text-[#c07a62] opacity-70" />
        </div>
        <h3 className="font-philosopher text-2xl font-bold text-[#2B1A14] mb-2">
            {query ? `No results for "${query}"` : "No packages yet"}
        </h3>
        <p className="text-sm text-[#7a5a4e] max-w-xs">
            {query
                ? "Try a different keyword or browse by category."
                : "We're curating something magical for you. Check back soon ✨"}
        </p>
    </div>
);

const UserPackages = () => {
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchInput, setSearchInput] = useState("");
    const [activeCategory, setActiveCategory] = useState("All");
    const [sortBy, setSortBy] = useState("default");
    const [showSortMenu, setShowSortMenu] = useState(false);
    const searchRef = useRef(null);
    const sortRef = useRef(null);
    const { openBookingModal } = usePackageBooking();

    const debouncedSearch = useDebounce(searchInput, 350);

    /* fetch */
    useEffect(() => {
        const fetchPackages = async () => {
            try {
                const res = await axios.get(`${process.env.REACT_APP_API_URL}/packages`);
                setPackages(res.data.data || []);
            } catch (err) {
                console.error("Failed to fetch packages", err);
            } finally {
                setLoading(false);
            }
        };
        fetchPackages();
    }, []);

    /* close sort menu on outside click */
    useEffect(() => {
        const handler = (e) => {
            if (sortRef.current && !sortRef.current.contains(e.target)) {
                setShowSortMenu(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    /* scroll to top on mount */
    useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, []);

    /* derived categories */
    const categories = ["All", ...Array.from(
        new Set(packages.filter(p => p.isActive && p.category?.trim()).map(p => p.category.trim()))
    )];

    /* filter + sort */
    const filtered = packages
        .filter(p => p.isActive)
        .filter(p => {
            const q = debouncedSearch.toLowerCase();
            if (!q) return true;
            return (
                p.name?.toLowerCase().includes(q) ||
                p.category?.toLowerCase().includes(q) 
            );
        })
        .filter(p => activeCategory === "All" || p.category?.trim() === activeCategory)
        .sort((a, b) => {
            const priceA = a.discountedPrice ?? a.price;
            const priceB = b.discountedPrice ?? b.price;
            if (sortBy === "price_asc") return priceA - priceB;
            if (sortBy === "price_desc") return priceB - priceA;
            if (sortBy === "discount") {
                const dA = a.discountedPrice ? a.price - a.discountedPrice : 0;
                const dB = b.discountedPrice ? b.price - b.discountedPrice : 0;
                return dB - dA;
            }
            return 0;
        });

    const SORT_OPTIONS = [
        { value: "default", label: "Default" },
        { value: "price_asc", label: "Price: Low → High" },
        { value: "price_desc", label: "Price: High → Low" },
        { value: "discount", label: "Best Discount" },
    ];

    return (
        <>


            <section className="relative min-h-screen bg-[#fdeee8] overflow-hidden">
                <FloatingPetals />

                {/* ── Hero header ── */}
                <div className="relative z-20 pt-10 pb-8 px-4 sm:px-8 md:px-12 lg:px-16">

                    {/* badge */}
                    <div className="flex justify-center mb-5">
                        <div className="flex items-center gap-2 border-2 border-white/50 bg-white/80 backdrop-blur-sm rounded-full text-[#ab310c] px-5 py-1.5 text-xs tracking-widest font-bold uppercase shadow-sm">
                            <img src={leaf} alt="leaf" className="w-4 h-4" />
                            <span>All Packages</span>
                            <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                        </div>
                    </div>

                    {/* title */}
                    <h1
                        className="text-center font-philosopher font-bold text-[#1a0f0a] leading-tight mb-3"
                        style={{
                            fontSize: "clamp(2rem, 5vw, 3.5rem)",
                            animation: "fadeSlideDown 0.5s ease both",
                        }}
                    >
                        Discover Our{" "}
                        <span className="relative inline-block">
                            <span className="bg-gradient-to-r from-[#ab310c] via-[#d44a20] to-[#EDB5A4] bg-clip-text text-transparent">
                                Beauty Packages
                            </span>
                            <svg
                                className="absolute -bottom-1 left-0 w-full"
                                height="6" viewBox="0 0 200 6"
                                preserveAspectRatio="none"
                            >
                                <path
                                    d="M0 5 Q50 0 100 5 Q150 10 200 5"
                                    stroke="url(#uline)" strokeWidth="2.5" fill="none"
                                />
                                <defs>
                                    <linearGradient id="uline" x1="0%" y1="0%" x2="100%" y2="0%">
                                        <stop offset="0%" stopColor="#ab310c" />
                                        <stop offset="100%" stopColor="#EDB5A4" />
                                    </linearGradient>
                                </defs>
                            </svg>
                        </span>
                    </h1>

                    <p
                        className="text-center text-sm text-[#7a5a4e] max-w-xl mx-auto leading-relaxed mb-8"
                        style={{ animation: "fadeSlideDown 0.55s 0.05s ease both", opacity: 0, animationFillMode: "forwards" }}
                    >
                        Expert services bundled at unbeatable value — hair, skin, grooming &amp; more.
                    </p>

                    {/* ── Search bar ── */}
                    <div
                        className="max-w-2xl mx-auto mb-5"
                        style={{ animation: "fadeSlideDown 0.55s 0.1s ease both", opacity: 0, animationFillMode: "forwards" }}
                    >
                        <div className="relative flex items-center">
                            <Search className="absolute left-4 w-4.5 h-4.5 text-[#9b6050] pointer-events-none" style={{ width: 18, height: 18 }} />
                            <input
                                ref={searchRef}
                                type="text"
                                value={searchInput}
                                onChange={e => setSearchInput(e.target.value)}
                                placeholder="Search packages, services, categories…"
                                className="
                  w-full pl-11 pr-10 py-3.5
                  rounded-2xl border border-[#9b3a1f]/20 focus:border-[#9b3a1f]
                  bg-white/80 backdrop-blur-sm 
                  text-sm text-[#2B1A14] placeholder-[#b8a39c]
                  shadow-sm outline-none
                  transition-all duration-200
                "
                            />
                            {searchInput && (
                                <button
                                    onClick={() => setSearchInput("")}
                                    className="absolute right-4 text-[#b8a39c] hover:text-[#9b3a1f] transition-colors"
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* ── Category pills + sort ── */}
                    <div
                        className="max-w-5xl mx-auto flex items-center gap-3"
                        style={{ animation: "fadeSlideDown 0.55s 0.15s ease both", opacity: 0, animationFillMode: "forwards" }}
                    >
                        {/* pills scroll */}
                        <div className="flex-1 flex gap-2 overflow-x-auto no-scrollbar py-1">
                            {categories.map(cat => (
                                <CategoryPill
                                    key={cat}
                                    label={cat}
                                    active={activeCategory === cat}
                                    onClick={() => setActiveCategory(cat)}
                                />
                            ))}
                        </div>

                        {/* sort dropdown */}
                        <div ref={sortRef} className="relative flex-shrink-0">
                            <button
                                onClick={() => setShowSortMenu(v => !v)}
                                className="flex items-center gap-2 px-3.5 py-2 rounded-xl border border-[#9b3a1f]/20 bg-white/80 backdrop-blur-sm text-xs font-semibold text-[#6b4535] hover:border-[#9b3a1f]/40 transition-all"
                            >
                                <SlidersHorizontal size={14} />
                                <span className="hidden sm:inline">Sort</span>
                            </button>
                            {showSortMenu && (
                                <div className="absolute right-0 top-10  bg-white rounded-2xl shadow-xl border border-[#9b3a1f]/10 overflow-hidden min-w-[170px]">
                                    {SORT_OPTIONS.map(opt => (
                                        <button
                                            key={opt.value}
                                            onClick={() => { setSortBy(opt.value); setShowSortMenu(false); }}
                                            className={`
                        w-full text-left px-4 py-2.5 text-xs font-medium transition-colors
                        ${sortBy === opt.value
                                                    ? "bg-[#2B1A14] text-[#f5e8e2]"
                                                    : "text-[#2B1A14] hover:bg-[#fdeee8]"
                                                }
                      `}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* result count */}
                    {!loading && (
                        <p className="max-w-5xl mx-auto mt-3 text-xs text-[#9b7a6e]">
                            {filtered.length} package{filtered.length !== 1 ? "s" : ""} found
                            {debouncedSearch ? ` for "${debouncedSearch}"` : ""}
                            {activeCategory !== "All" ? ` in ${activeCategory}` : ""}
                        </p>
                    )}
                </div>

                {/* ── Cards area ── */}
                <div className="relative z-10 px-4 sm:px-8 md:px-12 lg:px-16 pb-20">
                    {loading ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                            {Array.from({ length: 8 }).map((_, i) => (
                                <PackageCardSkeleton key={i} />
                            ))}
                        </div>
                    ) : filtered.length === 0 ? (
                        <EmptyState query={debouncedSearch} />
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                            {filtered.map((pkg, idx) => (
                                <div
                                    key={pkg._id}
                                    className="pkg-card-appear h-full"
                                    style={{ animationDelay: `${Math.min(idx * 0.06, 0.5)}s` }}
                                >
                                    <PackageCard pkg={pkg} openBookingModal={openBookingModal} />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* ── Decorative bottom wave ── */}
                <div className="absolute bottom-0 left-0 right-0 h-20 pointer-events-none">
                    <svg viewBox="0 0 1440 80" preserveAspectRatio="none" className="w-full h-full opacity-20">
                        <path d="M0 40 Q360 0 720 40 Q1080 80 1440 40 L1440 80 L0 80 Z" fill="#ab310c" />
                    </svg>
                </div>
            </section>

            <PackageBookingModal />

            {/* ── Keyframes injected once ── */}
            <style>{`
        @keyframes petalFloat {
          0%   { transform: translateY(0px)  rotate(0deg); }
          100% { transform: translateY(-18px) rotate(15deg); }
        }
        @keyframes fadeSlideDown {
          from { opacity: 0; transform: translateY(-12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .pkg-card-appear {
          animation: fadeSlideUp 0.35s ease both;
        }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
        </>
    );
};

export default UserPackages;